import React, { useEffect, useMemo, useState } from"react";
import { useParams, useNavigate } from"react-router-dom";
import { useTenant } from"../Context/TenantContext";
import API_BASE_URL from"../Config/config";
import { hasFullAccess, redirectToPaymentIfNeeded } from"../utils/paymentStatus";

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const LEVEL_ALIASES = {
 advance: ["Advance","Advanced"],
 advanced: ["Advanced","Advance"],
 intermediate: ["Intermediate"],
 basic: ["Basic"],
 expert: ["Expert"],
};

const PracticeLevelPage = () => {
  const { language, level } = useParams();
  const { tenant } = useTenant();
  const [topics, setTopics] = useState([]);
 const [selectedTopic, setSelectedTopic] = useState("");
 const [questions, setQuestions] = useState([]);
 const [effectiveLevel, setEffectiveLevel] = useState(null);
 const [blocked, setBlocked] = useState(false);

 const navigate = useNavigate();

 const normalizedLanguage = language.replace(/-/g,"").toLowerCase();

 const levelCandidates = useMemo(() => {
 const key = level.toLowerCase();
 return LEVEL_ALIASES[key] || [capitalize(key)];
 }, [level]);

 // Check if level requires premium (Advanced or Expert)
 const requiresPremium = useMemo(() => {
 const levelLower = level.toLowerCase();
 return levelLower ==="advanced" || levelLower ==="advance" || levelLower ==="expert";
 }, [level]);

  // Check payment status before loading - STRICT CHECK
  useEffect(() => {
    let alive = true;
    (async () => {
      // BYPASS FOR TENANT
      if (tenant) {
        setBlocked(false);
        return;
      }

      if (!requiresPremium) {
        setBlocked(false);
        return;
      }
 
 const user = JSON.parse(localStorage.getItem("user") ||"{}");
 const userId = user?.userId || user?.user_id ||"";
 
 if (!userId || userId ==="UnknownUser") {
 if (alive) {
 setBlocked(true);
 alert("Please log in to access premium challenges.");
 navigate("/skillgenpayments");
 }
 return;
 }

 try {
 console.log(`[Take Challenges] Checking premium access for ${userId}, level: ${level}`);
 const hasAccess = await hasFullAccess(userId);
 console.log(`[Take Challenges] Premium access result for ${userId}: ${hasAccess}`);
 if (alive) {
 if (!hasAccess) {
 console.log(`[Take Challenges] Access denied for ${userId} - blocking`);
 setBlocked(true);
 alert("Premium subscription required to access Advanced and Expert challenges.");
 navigate("/skillgenpayments");
 } else {
 console.log(`[Take Challenges] Access granted for ${userId}`);
 setBlocked(false);
 }
 }
 } catch (err) {
 console.error("[Take Challenges] Payment check failed:", err);
 // If payment check fails, block access to be safe
 if (alive) {
 setBlocked(true);
 alert("Unable to verify subscription. Please try again.");
 navigate("/skillgenpayments");
 }
 }
 })();
 return () => { alive = false; };
 }, [requiresPremium, navigate]);
 
 // Fetch topics on mount - ONLY if not blocked
 useEffect(() => {
 // Don't fetch if blocked for premium levels
 if (blocked && requiresPremium) {
 setTopics([]);
 setSelectedTopic("");
 setQuestions([]);
 return;
 }
 
 const fetchTopics = async () => {
 for (const lvl of levelCandidates) {
 try {
 const res = await fetch(`${API_BASE_URL}/practices/${normalizedLanguage}/${lvl}/topics`);
 if (!res.ok) throw new Error(`HTTP ${res.status}`);
 const data = await res.json();
 if (Array.isArray(data) && data.length > 0) {
 setTopics(data);
 setEffectiveLevel(lvl);
 return;
 }
 } catch (err) {
 console.error("Error loading topics:", err);
 }
 }
 setTopics([]);
 setEffectiveLevel(levelCandidates[0]);
 };

 fetchTopics();
 }, [normalizedLanguage, levelCandidates, blocked, requiresPremium]);
 
 // Fetch questions for selected topic
 const handleTopicSelect = async (topic) => {
 if (blocked) {
 alert("Premium subscription required to access Advanced and Expert challenges.");
 navigate("/skillgenpayments");
 return;
 }

 setSelectedTopic(topic);

 try {

 const targetLevel = effectiveLevel || levelCandidates[0];
 const res = await fetch(
 `${API_BASE_URL}/practices/${normalizedLanguage}/${targetLevel}/topic/${encodeURIComponent(topic)}`
 );

 const data = await res.json();
 
 if (Array.isArray(data)) {

 setQuestions(data);

 } else {

 console.warn("Expected array, but got:", data);

 setQuestions([]);

 }

 } catch (err) {

 console.error("Error loading questions:", err);

 setQuestions([]);

 }

 };
 
  if (blocked && requiresPremium && !tenant) {
    return (
      <div className="min-h-screen bg-bgMain flex items-center justify-center p-6">
 <div className="bg-bgSection p-12 rounded-[32px] border border-primary/30 shadow-2xl max-w-md w-full text-center relative overflow-hidden">
 <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl"></div>
 <div className="text-6xl mb-6">🔒</div>
 <h2 className="text-3xl font-extrabold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-4">Premium Access</h2>
 <p className="text-lg text-muted mb-8 leading-relaxed">
 Advanced and Expert challenges require a premium subscription. Unlock your coding potential today!
 </p>
 <button
 onClick={() => navigate("/skillgenpayments")}
 className="btn-primary-gradient w-full py-4 text-lg shadow-xl"
 >
 Upgrade to Premium
 </button>
 <button
 onClick={() => navigate(-1)}
 className="mt-6 text-muted hover:text-primary text-sm font-bold transition-all block mx-auto"
 >
 ← Go Back
 </button>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-bgMain p-8 md:p-16 relative overflow-hidden font-sans">
 {/* Background decoration */}
 <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48"></div>
 
 {/* Back Button */}
 <button
 onClick={() => navigate(-1)}
 className="mb-10 text-muted hover:text-primary flex items-center gap-2 font-bold transition-colors group"
 >
 <span className="w-8 h-8 rounded-full bg-bgSection flex items-center justify-center group-hover:bg-primary/20 transition-all">←</span>
 Back to Technologies
 </button>

 {/* Header */}
 <div className="mb-12 relative z-10">
 <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent uppercase tracking-tighter mb-2">
 {capitalize(language.replace(/-/g,""))}
 </h1>
 <p className="text-primary font-bold tracking-widest text-sm uppercase">{effectiveLevel || levelCandidates[0]} Level Challenges</p>
 </div>
 
 {/* Topic Dropdown */}
 <div className="max-w-4xl mb-12 relative z-10">
 <label className="block text-muted text-xs font-bold uppercase tracking-widest mb-3 ml-1">Select a Topic</label>
 <select
 value={selectedTopic}
 onChange={(e) => handleTopicSelect(e.target.value)}
 className="w-full md:w-1/2 p-4 bg-bgSection text-body font-bold border border-accentGray/50 rounded-2xl focus:border-primary outline-none transition-all cursor-pointer shadow-xl appearance-none"
 style={{ backgroundImage:"url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748B%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')", backgroundRepeat:"no-repeat", backgroundPosition:"right 1rem center", backgroundSize:"1.5em" }}
 >
 <option value="" className="bg-bgSection">-- Choose a topic to begin --</option>
 {topics.map((topic, index) => (
 <option key={index} value={topic} className="bg-bgSection">
 {topic}
 </option>
 ))}
 </select>
 </div>
 
 {/* Questions List */}
 <div className="max-w-4xl flex flex-col gap-6 relative z-10 mb-20">
 {selectedTopic && Array.isArray(questions) && questions.length === 0 && (
 <div className="p-8 bg-bgSection rounded-3xl border border-accentGray/30 text-muted font-bold text-center">
 No questions found for this topic yet. Stay tuned!
 </div>
 )}
 
 {Array.isArray(questions) && questions.map((q) => (
 <div
 key={q.id}
 className="bg-bgSection border-2 border-accentGray/30 rounded-3xl p-6 md:p-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 transition-all duration-300 hover:border-primary/30 hover:shadow-[0_0_30px_rgba(79, 70, 229,0.1)] group"
 >
 <div>
 <h3 className="text-xl font-bold text-heading group-hover:text-primary transition-colors mb-1">{q.title}</h3>
 <p className="text-muted text-sm font-medium">{q.topic || selectedTopic}</p>
 </div>
 <button
 onClick={() => navigate(`/practices/${language}/${level}/${q.id}`)}
 className="btn-primary-gradient px-10 py-3 rounded-xl font-bold shadow-lg hover:scale-105 active:scale-95 transition-all w-full sm:w-auto text-center"
 >
 Solve Now
 </button>
 </div>
 ))}
 </div>
 </div>
 );
};

export default PracticeLevelPage;




